import { useQuery } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions';
import { InlineReferralReview } from './InlineReferralReview';
import { IncomingSettings } from './IncomingSettings';

export function FaxProcessingDetails({ documentId }: { documentId: string }) {
  const { organization } = useAuth();
  const { can } = usePermissions();
  const org = organization?.organization_id;
  const allowed =
    !!org && can('use_assistant') && can('read_agent_proposal') && can('read_patient') && can('read_document');
  const query = useQuery({
    queryKey: ['fax-processing', org, documentId],
    gcTime: 0,
    enabled: allowed,
    refetchInterval: 10000,
    queryFn: async () =>
      (
        await clientApi.get<{
          item: { status: string; provisional: boolean; job_id: string | null } | null;
          automatic: boolean;
          provisional_intake: boolean;
        }>(`/api/v1/organizations/${org}/assistant/incoming/documents/${documentId}`)
      ).data,
  });
  if (!allowed) return null;
  if (query.isError)
    return (
      <p role="alert">
        Fax processing details could not be loaded. Your practice reviewer can check processing access.
      </p>
    );
  if (!query.data?.item) return null;
  return (
    <section className="flex flex-col gap-3" aria-label="Fax processing details">
      <h3>Automatic fax processing</h3>
      <p>
        Analysis: {query.data.automatic ? 'automatic' : 'manual'}. Provisional preparation:{' '}
        {query.data.provisional_intake ? 'enabled' : 'disabled'}.
      </p>
      <p>
        Status: {query.data.item.status.replaceAll('_', ' ')}
        {query.data.item.provisional ? ' · Provisional chart — approval required' : ''}
      </p>
      <IncomingSettings org={org!} />
      {query.data.item.job_id && <InlineReferralReview org={org!} jobId={query.data.item.job_id} />}
    </section>
  );
}
